import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {getLockIconConfig} from '@application/helper/get-lock-icon-config';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {NavigationBreadcrumbsService} from '@application/services/navigation-breadcrumbs/navigation-breadcrumbs.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {BreakTime} from '@domain/calendar/pattern/break-time';
import {Pattern} from '@domain/calendar/pattern/pattern';
import {PatternShift} from '@domain/calendar/pattern/pattern-shift';
import {Shift} from '@domain/calendar/shift/shift';
import {HttpPatternsService} from '@infrastructure/http/calendar/pattern/http-patterns.service';
import {TextileService} from '@presentation/pages/textile-data/textile-data-overview/textile.service';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {
  AgGridRowSelection,
  AssertionUtils,
  BackendError,
  BaseComponent,
  ButtonAction,
  CanLeavePage,
  CheckboxCellEditorComponent,
  ColDefBuilderFactoryService,
  ContentSwitcherNavigationInput,
  CrudOverviewButtonConfig,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  HeaderCheckboxCellRendererComponent,
  LockIconConfig,
  NavigationHelperPrototypeService,
  noSpaceValidator,
  SaveType,
  ScheduleItem,
  TabsMenuItem,
  TranslateService,
  UuidUtils
} from '@vdw/angular-component-library';
import {ColDef, GridApi, GridReadyEvent, IRowDragItem, IRowNode, RowDataUpdatedEvent, RowDragEnterEvent, RowDragEvent, RowNode, ValueGetterParams} from 'ag-grid-community';
import {cloneDeep} from 'lodash-es';
import {Duration} from 'luxon';
import {finalize, Observable, takeUntil} from 'rxjs';
import {CalendarTimelineService} from '../../calendar-timeline.service';
import {PatternShiftRowData} from './pattern-shift-row-data.interface';

@Component({
  templateUrl: 'add-pattern-page.component.html',
  styleUrl: 'add-pattern-page.component.scss',
  providers: [CalendarTimelineService]
})
export class AddPatternPageComponent extends BaseComponent implements OnInit, CanLeavePage {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;
  @Input() public canShowHeader: boolean = false;

  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_SHIFT_PATTERN;
  public readonly PATTERN_TRANSLATION_KEY = NavigationUtils.getNavigationData(NavigationId.PATTERN_MODULE).navigationKey;
  public readonly SAVE_TYPE = SaveType;
  public readonly DEFAULT_START_TIME = '06:00';
  private readonly DEFAULT_NUM_DAYS = 1;
  private readonly DAY_COLUMN_WIDTH = 32;
  private readonly DAY_COLUMN_ID = 'day';
  private readonly SELECTED_ITEM_LIST_NAME = 'scheduleItems';

  public addPatternForm: FormGroup<{name: FormControl<string>; numDays: FormControl<number>; startTime: FormControl<string>}>;
  public generalTab: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};
  public config: CrudOverviewDataConfig<PatternShiftRowData>;
  public lockIconConfig: LockIconConfig;
  public isLocked = false;
  private gridApi: GridApi;
  private patternToSave: Pattern;
  private initialDraggedRowIndex: number;
  private newDraggedRowIndex: number;

  public constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    public readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<PatternShiftRowData>,
    private readonly calendarTimelineService: CalendarTimelineService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly formBuilder: FormBuilder,
    private readonly patterns: HttpPatternsService,
    private readonly removeConfirmation: RemoveConfirmationService,
    private readonly router: Router,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService,
    private readonly navigationBreadcrumbsService: NavigationBreadcrumbsService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper
  ) {
    super();
  }

  public get createdObject(): string {
    return !AssertionUtils.isNullOrUndefined(this.addPatternForm.controls.name.value) ? this.addPatternForm.controls.name.value : '';
  }

  public ngOnInit(): void {
    this.setFormFields();
    this.config = this.builderFactoryService
      .getBuilder()
      .withDefaultButtons({crudOverviewButtonConfigs: this.getButtonConfig()})
      .withGridOptions(this.getColumnDefs(), GridIdentifier.PATTERN_SHIFT, [{scheduleItems: []}], (builder: GridOptionsBuilder) =>
        builder
          .withoutGetRowId()
          .withOnGridReady((event: GridReadyEvent) => (this.gridApi = event.api))
          .withOnRowDataUpdated((event: RowDataUpdatedEvent) => {
            this.calendarTimelineService.updateTotalHours(event.api.getRenderedNodes().map((rowNode: IRowNode) => rowNode.data));
            event.api.refreshHeader();
          })
          .withOnGridReady(() => this.gridApi.updateGridOptions({rowSelection: {mode: AgGridRowSelection.MULTIPLE, headerCheckbox: false, checkboxes: false}}))
          .withRowDraggingInsideGrid((params: IRowDragItem) => (params.rowNode.rowIndex + 1).toString())
          .withOnRowDragEnter((event: RowDragEnterEvent) => {
            if (AssertionUtils.isNullOrUndefined(this.initialDraggedRowIndex)) {
              this.initialDraggedRowIndex = event.node.rowIndex;
            }
          })
          .withOnRowDragEndOrLeave((event: RowDragEvent) => (this.newDraggedRowIndex = event.node.rowIndex))
          .withOnDragStopped(() => this.onDragStopped())
          .build()
      )
      .withoutSelectedToggle()
      .withSelectedItemListName(this.SELECTED_ITEM_LIST_NAME)
      .build();

    const id = this.activatedRoute.snapshot?.params?.id ?? this.itemId;
    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.patterns
        .getById(parseInt(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((pattern: Pattern) => {
          this.isLocked = this.isEditingPattern() && pattern?.used;
          this.itemId = pattern.id;
          this.patternToSave = pattern;
          this.setFormValues();
          this.setPatternShiftRowData();
          this.setLockIconConfig();
        });
    }
  }

  public canLeavePage(): boolean {
    return !this.addPatternForm.dirty || this.saving;
  }

  public savePattern(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addPatternForm], this.document);
    if (isValid) {
      const patternToSave = this.getCurrentPattern();
      this.saving = true;

      const request: Observable<void | number> = this.isEditingPattern() ? this.patterns.update(patternToSave) : this.patterns.save(patternToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.isEditingPattern() ? this.finalizeSaving() : null)).subscribe({
        next: (id: number) => {
          if (this.navigationHelper.contentSwitcherDialogActive()) {
            if (saveType === SaveType.SAVE_AND_CLOSE) {
              patternToSave.id = id;
              this.navigationHelper.navigateBack(this.isEditingPattern() ? [] : patternToSave);
            } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
              this.pageType = PageTypeEnum.ADD;
              this.setFormFields();
              this.setPatternShiftRowData();
              this.saving = false;
            }
            return;
          }
          this.textileService.navigateAndShowToast(saveType, TextileDataType.SHIFT_PATTERN, this.PATTERN_TRANSLATION_KEY, this.isEditingPattern(), patternToSave.name, id);
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingPattern() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public duplicatePattern(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateWithContentSwitcher({
        componentInfo: {
          component: AddPatternPageComponent,
          componentInputs: {
            itemId: this.itemId,
            pageType: PageTypeEnum.DUPLICATE,
            canShowHeader: true
          }
        },
        forgetCurrentEntry: true
      });
    } else {
      this.navigationHelper.navigateWithRoute({
        route: NavigationUtils.getAbsolutePath(NavigationId.DUPLICATE_PATTERN).replace(':id', this.patternToSave.id.toString()),
        forgetCurrentEntry: true
      });
    }
  }

  public deletePattern(): void {
    const data = this.getCurrentPattern();
    this.removeConfirmation.open(data, this.patterns.delete(data.id), this.PATTERN_TRANSLATION_KEY, null, null, true);
  }

  public navigateBack(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateBack();
    } else {
      this.router.navigateByUrl(NavigationUtils.getAbsolutePath(NavigationId.PATTERN));
    }
  }

  public onNumDaysBlurred(): void {
    if (this.addPatternForm.value.numDays === null || this.addPatternForm.value.numDays < 1) {
      this.addPatternForm.controls.numDays.reset(this.DEFAULT_NUM_DAYS);
    }
    this.setPatternShiftRowData();
  }

  public onStartTimeBlurred(): void {
    if (AssertionUtils.isEmpty(this.addPatternForm.value.startTime)) {
      this.addPatternForm.controls.startTime.reset(this.DEFAULT_START_TIME);
    }
    this.setPatternShiftRowData();
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(NavigationUtils.getAbsolutePath(NavigationId.EDIT_PATTERN), this.addPatternForm.controls.name);
  }

  public getLoadEntry(): ContentSwitcherNavigationInput {
    const editRoute = NavigationUtils.getNavigationRoute(NavigationId.EDIT_PATTERN);

    return {
      componentInfo: {
        component: editRoute.component,
        componentInputs: {pageType: PageTypeEnum.EDIT, itemId: this.addPatternForm?.controls?.name?.errors?.entityId, canShowHeader: true}
      }
    };
  }

  public isEditingPattern(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.EDIT;
    }
    return this.activatedRoute.snapshot.routeConfig.path === NavigationUtils.getRoute(NavigationId.EDIT_PATTERN);
  }

  private setFormFields(): void {
    this.addPatternForm = this.formBuilder.group({
      name: this.formBuilder.control(null, [Validators.required, noSpaceValidator], AsyncUniqueValidator.createValidator(this.patterns, null)),
      numDays: this.formBuilder.control(this.DEFAULT_NUM_DAYS),
      startTime: this.formBuilder.control(this.DEFAULT_START_TIME)
    });

    this.patternToSave = new Pattern(null, null, null, null, []);
    this.generalTab.form = this.addPatternForm;
  }

  private setFormValues(): void {
    const name = this.isEditingPattern() ? this.patternToSave.name : null;

    this.addPatternForm.reset({
      name,
      numDays: this.patternToSave.numDays,
      startTime: this.patternToSave.startTime.toFormat('hh:mm')
    });

    this.addPatternForm.controls.name.setAsyncValidators(AsyncUniqueValidator.createValidator(this.patterns, name));

    if (this.isLocked) {
      this.addPatternForm.disable();
      this.gridApi?.updateGridOptions({columnDefs: this.getColumnDefs().slice(1)});
    }
  }

  private setPatternShiftRowData(): void {
    const patternShiftRowData: PatternShiftRowData[] = [];

    for (let i = 1; i <= this.addPatternForm.value.numDays; i++) {
      patternShiftRowData.push({scheduleItems: [], isLocked: this.isLocked});
    }

    const patternStartTime = Duration.fromISOTime(this.addPatternForm.value.startTime);
    if (patternStartTime.isValid) {
      this.patternToSave.patternShifts.sort((patternShiftA: PatternShift, patternShiftB: PatternShift) => patternShiftA.sequence - patternShiftB.sequence);
      this.patternToSave.patternShifts.forEach((patternShift: PatternShift) => {
        if (
          patternShift.dayIndex < this.addPatternForm.value.numDays &&
          patternShift.startTime >= patternStartTime &&
          patternShift.endTime <= patternStartTime.plus({days: 1, hours: 23, minutes: 59})
        ) {
          const currentRowNode = this.gridApi?.getDisplayedRowAtIndex(patternShift.dayIndex);
          const selected = currentRowNode?.data.scheduleItems.find((scheduleItem: ScheduleItem) => scheduleItem.id === patternShift.id)?.selected;
          patternShiftRowData[patternShift.dayIndex].scheduleItems.push(this.calendarTimelineService.getScheduleItemFromShift(patternShift, selected));
        }
      });

      this.calendarTimelineService.startTime = patternStartTime;
      this.calendarTimelineService.updateTotalHours(patternShiftRowData);

      if (AssertionUtils.isNullOrUndefined(this.gridApi)) {
        this.config.rowData = patternShiftRowData;
      } else {
        this.gridApi.setGridOption('rowData', patternShiftRowData);
        this.gridApi.redrawRows();
      }
    }
  }

  private getCurrentPattern(): Pattern {
    const patternShifts: PatternShift[] = [];
    this.gridApi?.forEachNode(({data}: IRowNode, rowIndex: number) => {
      const selectedShiftNames: string[] = [];
      data.scheduleItems.forEach((scheduleItem: ScheduleItem, index: number) => {
        selectedShiftNames.push(scheduleItem.name);
        patternShifts.push(
          new PatternShift(
            scheduleItem.id,
            rowIndex,
            this.patternToSave.patternShifts.flatMap((patternShift: PatternShift) => patternShift.shift).find((shift: Shift) => shift.name === scheduleItem.name),
            scheduleItem.startTime,
            scheduleItem.endTime,
            scheduleItem.breaks as BreakTime[],
            index + 1,
            selectedShiftNames.filter((name: string) => name === scheduleItem.name).length
          )
        );
      });
    });
    return new Pattern(
      this.isEditingPattern() ? this.patternToSave.id : null,
      this.addPatternForm.value.name,
      this.addPatternForm.value.numDays,
      Duration.fromISOTime(this.addPatternForm.value.startTime),
      patternShifts
    );
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.PATTERN_TRANSLATION_KEY, {count: 1})
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private getColumnDefs(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('selected')
        .withHeaderName('')
        .withHeaderComponent(HeaderCheckboxCellRendererComponent)
        .withCellRenderer(CheckboxCellEditorComponent, {canSetRowSelected: true, selectedItemListName: this.SELECTED_ITEM_LIST_NAME})
        .withWidth(40)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('left')
        .withSuppressHeaderMenuButton()
        .withSortable(false)
        .withCellClass('ag-cell-flex-center')
        .withValueGetter((params: ValueGetterParams) => params.node.isSelected())
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId(this.DAY_COLUMN_ID)
        .withHeaderName('')
        .withValueGetter((params: ValueGetterParams) => params.node.rowIndex + 1, true)
        .withCellClass('no-padding text-align-center')
        .withMaxWidth(this.DAY_COLUMN_WIDTH)
        .withResizable(false)
        .withoutFilter()
        .withSuppressMovable('left')
        .withSuppressHeaderMenuButton()
        .build(),
      this.calendarTimelineService.getColumnDef(
        this.colDefBuilderFactoryService,
        this.unSubscribeOnViewDestroy,
        (_: IRowNode, patternShifts: PatternShift[]) => this.addPatternShift(patternShifts),
        (_: IRowNode, patternShifts: PatternShift[]) => this.editPatternShift(patternShifts),
        ({id}: ScheduleItem) => this.deletePatternShift(id),
        () => this.patternToSave.patternShifts
      )
    ];
  }

  private addPatternShift(patternShifts: PatternShift[]): void {
    if (!AssertionUtils.isNullOrUndefined(patternShifts)) {
      this.patternToSave.patternShifts.push(...patternShifts);
      patternShifts.forEach((shift: PatternShift) => {
        const filteredPatternShifts = this.patternToSave.patternShifts.filter((patternShift: PatternShift) => patternShift.dayIndex === shift.dayIndex);
        filteredPatternShifts.sort((patternShiftA: PatternShift, patternShiftB: PatternShift) => patternShiftA.startTime.as('minutes') - patternShiftB.startTime.as('minutes'));
        filteredPatternShifts.forEach((patternShift: PatternShift, index: number) => (patternShift.sequence = index + 1));
      });
    }
  }

  private deletePatternShift(id: number | string): void {
    this.patternToSave.patternShifts = this.patternToSave.patternShifts.filter((patternShift: PatternShift) => patternShift.id !== id);
  }

  private editPatternShift(patternShifts: PatternShift[]): void {
    if (!AssertionUtils.isNullOrUndefined(patternShifts)) {
      this.deletePatternShift(patternShifts[0].id);
      this.addPatternShift(patternShifts);
    }
  }

  private setLockIconConfig(): void {
    if (this.isLocked) {
      this.lockIconConfig = getLockIconConfig(this.patternToSave.name, this.patterns.getConflicts(this.patternToSave.id));
      this.navigationBreadcrumbsService.onLocked(this.lockIconConfig);
    }
  }

  private getButtonConfig(): CrudOverviewButtonConfig[] {
    const buttons = this.gridActionButtonsHelper.getActionButtonsForGridSelection({
      requiredPermissionToEdit: NavigationUtils.getNavigationData(NavigationId.ADD_PATTERN).requiredPermission,
      withDuplicate: (grid: GridButtonClickedEvent) => this.duplicate(grid),
      withDelete: (grid: GridButtonClickedEvent) => this.delete(grid),
      allowDeleteMultiple: true
    });

    const duplicateButton = buttons.find((button: CrudOverviewButtonConfig) => button.action === ButtonAction.DUPLICATE);
    duplicateButton.onlyEnabledOnSingleRowSelection = false;
    duplicateButton.disabledCondition = (): boolean => this.canDisableDuplicateButton();

    return buttons;
  }

  private duplicate({rowNode}: GridButtonClickedEvent): void {
    const selectedRowIndex = AssertionUtils.isNullOrUndefined(rowNode)
      ? this.gridApi.getRenderedNodes().find((node: RowNode) => node.data.scheduleItems.some((item: ScheduleItem) => item.selected)).rowIndex
      : rowNode.rowIndex;
    const previousDayEndTime = this.calendarTimelineService.getPreviousDayEndTime(this.gridApi, selectedRowIndex + 1);
    const nextDayStartTime = this.calendarTimelineService.getNextDayStartTime(this.gridApi, selectedRowIndex);
    let patternShiftsToDuplicate: PatternShift[] = null;

    if (rowNode) {
      patternShiftsToDuplicate = cloneDeep(this.patternToSave.patternShifts).filter((shift: PatternShift) => shift.dayIndex === rowNode.rowIndex);
    } else {
      const patternShiftIdsToDuplicate = this.gridApi
        .getDisplayedRowAtIndex(selectedRowIndex)
        .data.scheduleItems.filter((item: ScheduleItem) => item.selected)
        .map((item: ScheduleItem) => item.id);
      patternShiftsToDuplicate = cloneDeep(this.patternToSave.patternShifts).filter((patternShift: PatternShift) => patternShiftIdsToDuplicate.includes(patternShift.id));
    }
    patternShiftsToDuplicate = patternShiftsToDuplicate
      .filter((shift: PatternShift) => !(shift.startTime < previousDayEndTime || shift.endTime > nextDayStartTime))
      .sort((shiftA: PatternShift, shiftB: PatternShift) => shiftA.sequence - shiftB.sequence);

    const scheduleItems = patternShiftsToDuplicate.map((patternShift: PatternShift) => {
      patternShift.dayIndex = selectedRowIndex + 1;
      patternShift.id = UuidUtils.generateV4Uuid();
      patternShift.breakTimes.forEach((breakTime: BreakTime) => (breakTime.id = UuidUtils.generateV4Uuid()));
      return this.calendarTimelineService.getScheduleItemFromShift(patternShift);
    });

    this.addPatternForm.controls.numDays.reset(this.addPatternForm.value.numDays + 1);
    this.gridApi.applyTransaction({add: [{scheduleItems}], addIndex: selectedRowIndex + 1});
    this.gridApi.refreshCells({columns: [this.DAY_COLUMN_ID]});
    this.patternToSave.patternShifts.filter((shift: PatternShift) => shift.dayIndex > selectedRowIndex).forEach((shift: PatternShift) => shift.dayIndex++);
    this.patternToSave.patternShifts.push(...patternShiftsToDuplicate);
  }

  private canDisableDuplicateButton(): boolean {
    const selectedRowNodes = this.gridApi?.getRenderedNodes().filter((node: RowNode) => node.data?.scheduleItems?.some((item: ScheduleItem) => item.selected));
    return selectedRowNodes.length > 1;
  }

  private delete({rowNode}: GridButtonClickedEvent): void {
    if (rowNode) {
      const dayIndexToDeleted = rowNode.rowIndex;
      const previousDayEndTime = this.calendarTimelineService.getPreviousDayEndTime(this.gridApi, rowNode.rowIndex);
      const nextRow = this.gridApi.getDisplayedRowAtIndex(dayIndexToDeleted + 1);
      const removedIds = [];
      const filteredItems = [
        ...(nextRow?.data?.scheduleItems?.filter((scheduleItem: ScheduleItem) => {
          if (scheduleItem.startTime < previousDayEndTime) {
            removedIds.push(scheduleItem.id);
            return false;
          }
          return true;
        }) ?? [])
      ];

      this.patternToSave.patternShifts = this.patternToSave.patternShifts.filter((patternShift: PatternShift) => patternShift.dayIndex !== dayIndexToDeleted && !removedIds.includes(patternShift.id));
      this.patternToSave.patternShifts.forEach((patternShift: PatternShift) => (patternShift.dayIndex = patternShift.dayIndex > dayIndexToDeleted ? patternShift.dayIndex - 1 : patternShift.dayIndex));
      this.addPatternForm.controls.numDays.reset(Math.max(this.addPatternForm.value.numDays - 1, 1));

      this.gridApi.applyTransaction({remove: [rowNode.data]});
      if (this.gridApi.getRenderedNodes().length === 0) {
        this.gridApi.applyTransaction({add: [{scheduleItems: []}]});
      }
      this.gridApi.refreshCells({columns: [this.DAY_COLUMN_ID]});

      if (removedIds.length > 0) {
        nextRow.setDataValue('scheduleItems', filteredItems);
      }
    } else {
      const removedIds = [];
      const patternShiftRowData = this.gridApi.getRenderedNodes().map((node: RowNode) => {
        const filteredItems = node.data.scheduleItems.filter((item: ScheduleItem) => {
          if (item.selected) {
            removedIds.push(item.id);
          }
          return !item.selected;
        });

        return {scheduleItems: filteredItems};
      });

      this.patternToSave.patternShifts = this.patternToSave.patternShifts.filter((patternShift: PatternShift) => !removedIds.includes(patternShift.id));
      this.gridApi.deselectAll();
      this.gridApi.setGridOption('rowData', patternShiftRowData);
      this.gridApi.redrawRows();
    }
  }

  private onDragStopped(): void {
    if (this.initialDraggedRowIndex !== this.newDraggedRowIndex) {
      const patternShiftRowData = this.gridApi.getRenderedNodes().map((rowNode: IRowNode) => rowNode.data);

      [this.newDraggedRowIndex, this.initialDraggedRowIndex].forEach((rowIndex: number) => {
        const previousDayEndTime = patternShiftRowData[rowIndex - 1]?.scheduleItems?.at(-1)?.endTime?.minus({days: 1});
        const nextDayStartTime = patternShiftRowData[rowIndex + 1]?.scheduleItems?.at(0)?.startTime?.plus({days: 1});
        const removedIds = [];
        patternShiftRowData[rowIndex].scheduleItems = patternShiftRowData[rowIndex].scheduleItems.filter((scheduleItem: ScheduleItem) => {
          if (scheduleItem.startTime < previousDayEndTime || scheduleItem.endTime > nextDayStartTime) {
            removedIds.push(scheduleItem.id);
            return false;
          }
          return true;
        });

        if (removedIds.length > 0) {
          this.patternToSave.patternShifts = this.patternToSave.patternShifts.filter((shift: PatternShift) => !removedIds.includes(shift.id));
        }
      });

      this.patternToSave.patternShifts
        .filter(
          (shift: PatternShift) => shift.dayIndex >= Math.min(this.initialDraggedRowIndex, this.newDraggedRowIndex) && shift.dayIndex <= Math.max(this.initialDraggedRowIndex, this.newDraggedRowIndex)
        )
        .forEach((shift: PatternShift) => {
          if (shift.dayIndex === this.initialDraggedRowIndex) {
            shift.dayIndex = this.newDraggedRowIndex;
          } else {
            shift.dayIndex = shift.dayIndex + (this.newDraggedRowIndex > this.initialDraggedRowIndex ? -1 : 1);
          }
        });

      this.gridApi.setGridOption('rowData', patternShiftRowData);
      this.gridApi.redrawRows();
    }
    this.initialDraggedRowIndex = null;
  }
}
